import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, LinearProgress, Stack, Typography } from '@mui/joy';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';

import { useAdvertiser } from '../../AdvertiserContext';
import { APIAdsDepositDetail } from '../../api_ads';
import { formatMoney } from './adsConstants';


const POLL_INTERVAL_MS = 3000;
const MAX_ATTEMPTS = 20; // ~1 минута


/**
 * Обработка возврата с формы оплаты.
 *
 * Вебхук ЮKassa приходит асинхронно и вполне может опоздать относительно
 * возврата пользователя. Показать «ждите» — значит показать нулевой баланс
 * сразу после успешной оплаты. Поэтому опрашиваем статус: эндпоинт
 * при pending сам сверяется с ЮKassa и зачисляет деньги.
 *
 * Опрос ограничен по времени: бесконечный polling в незакрытой вкладке
 * это фоновые запросы неделями.
 */
export default function AdDepositReturnHandler({ depositId, onSettled, onDismiss }) {
    const { advertiserToken: token } = useAdvertiser();

    const [deposit, setDeposit] = useState(null);
    const [attempts, setAttempts] = useState(0);
    const [timedOut, setTimedOut] = useState(false);
    const [error, setError] = useState(null);
    const [checking, setChecking] = useState(false);
    const settledRef = useRef(false);

    const check = useCallback(async () => {
        if (!token || !depositId) return null;
        try {
            setChecking(true);
            const res = await APIAdsDepositDetail(token, depositId);
            const loaded = res?.deposit || null;
            setDeposit(loaded);
            return loaded;
        } catch (e) {
            setError(e?.response?.status === 404
                ? 'Платёж не найден'
                : 'Не удалось проверить статус платежа');
            return null;
        } finally {
            setChecking(false);
        }
    }, [token, depositId]);

    useEffect(() => {
        if (!depositId || settledRef.current || timedOut) return undefined;

        let cancelled = false;
        const run = async () => {
            const loaded = await check();
            if (cancelled) return;

            const isFinal = loaded && ['paid', 'canceled', 'failed', 'refunded'].includes(loaded.status);
            if (isFinal) {
                settledRef.current = true;
                onSettled?.(loaded);
                return;
            }
            if (attempts + 1 >= MAX_ATTEMPTS) {
                setTimedOut(true);
                return;
            }
            setTimeout(() => {
                if (!cancelled) setAttempts((n) => n + 1);
            }, POLL_INTERVAL_MS);
        };

        run();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [depositId, attempts, timedOut]);

    if (!depositId) return null;

    if (error) {
        return (
            <Alert color="danger" sx={{ mb: 2 }} endDecorator={
                <Button size="sm" variant="plain" color="danger" onClick={onDismiss}>Скрыть</Button>
            }>
                {error}
            </Alert>
        );
    }

    if (deposit?.status === 'paid') {
        return (
            <Alert
                color="success"
                startDecorator={<CheckCircleRoundedIcon />}
                sx={{ mb: 2 }}
                endDecorator={
                    <Button size="sm" variant="plain" color="success" onClick={onDismiss}>Понятно</Button>
                }
            >
                <Box>
                    <Typography level="title-sm">
                        Счёт пополнен на {formatMoney(deposit.amount)} ₽
                    </Typography>
                    <Typography level="body-sm">
                        Деньги зачислены. Кампании, остановленные из-за нехватки средств,
                        возобновлены автоматически.
                    </Typography>
                </Box>
            </Alert>
        );
    }

    if (deposit && ['canceled', 'failed'].includes(deposit.status)) {
        return (
            <Alert
                color="danger"
                startDecorator={<ErrorRoundedIcon />}
                sx={{ mb: 2 }}
                endDecorator={
                    <Button size="sm" variant="plain" color="danger" onClick={onDismiss}>Скрыть</Button>
                }
            >
                <Box>
                    <Typography level="title-sm">Платёж не прошёл</Typography>
                    <Typography level="body-sm">
                        {deposit.comment || 'Оплата отменена или отклонена банком. Попробуйте ещё раз.'}
                    </Typography>
                </Box>
            </Alert>
        );
    }

    if (timedOut) {
        return (
            <Alert color="warning" sx={{ mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <Typography level="title-sm">Платёж ещё обрабатывается</Typography>
                    <Typography level="body-sm" sx={{ mb: 1 }}>
                        Банк подтверждает операцию дольше обычного. Деньги зачислятся
                        автоматически — можно закрыть страницу и вернуться позже.
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        <Button
                            size="sm"
                            variant="soft"
                            color="warning"
                            loading={checking}
                            onClick={async () => {
                                const loaded = await check();
                                if (loaded && loaded.status !== 'pending') {
                                    settledRef.current = true;
                                    onSettled?.(loaded);
                                }
                            }}
                        >
                            Проверить сейчас
                        </Button>
                        <Button size="sm" variant="plain" color="warning" onClick={onDismiss}>
                            Скрыть
                        </Button>
                    </Stack>
                </Box>
            </Alert>
        );
    }

    return (
        <Alert color="primary" sx={{ mb: 2 }}>
            <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <CircularProgress size="sm" />
                    <Box>
                        <Typography level="title-sm">Проверяем оплату…</Typography>
                        <Typography level="body-sm">
                            Обычно это занимает несколько секунд
                        </Typography>
                    </Box>
                </Stack>
                <LinearProgress
                    determinate
                    value={(attempts / MAX_ATTEMPTS) * 100}
                    sx={{ mt: 1 }}
                />
            </Box>
        </Alert>
    );
}